import {
	CAPTIONLESS_MEDIA_KINDS,
	fitsCaption,
	MAX_BROADCAST_BUTTONS,
	MAX_BUTTON_LABEL_LENGTH,
	MAX_CAPTION_LENGTH,
	MAX_GALLERY_ITEMS,
	MAX_TEXT_LENGTH,
	MIN_GALLERY_ITEMS,
	visibleTextLength,
} from "@velizhanin-dev/bot-scenario"
import { AnimatePresence, motion } from "framer-motion"
import { Check, Plus, Trash2 } from "lucide-react"
import type { ReactNode } from "react"
import { makeButtonId } from "@/entities/bot-builder"
import {
	type BroadcastDraftButton,
	type BroadcastDraftContent,
	broadcastMediaSource,
} from "@/entities/broadcast"
import { GalleryField, MediaField } from "@/features/edit-scenario-draft"
import { listItem } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { LoadSwap } from "@/shared/ui/load-swap"
import { NativeSelect } from "@/shared/ui/native-select"
import { Reveal } from "@/shared/ui/reveal"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { TextWithVariables } from "@/shared/ui/text-with-variables"
import { ToolButton } from "@/shared/ui/tool-button"
import { Section } from "./section"

const MEDIA_MODES = [
	{ value: "single", label: "Один файл" },
	{ value: "gallery", label: "Альбом" },
] as const

const OPTIONS = [
	{ key: "silent", label: "Без звука уведомления" },
	{ key: "protect", label: "Запретить пересылку и сохранение" },
	{ key: "noLinkPreview", label: "Без превью ссылок" },
] as const satisfies readonly { key: keyof BroadcastDraftContent; label: string }[]

/** Подзаголовок внутри карточки: подпись слева, счётчик или действие справа. */
function Part({ title, aside }: { title: string; aside?: ReactNode }) {
	return (
		<div className="mb-2 flex items-center justify-between gap-3">
			<h3 className="text-sm font-medium text-foreground/90">{title}</h3>
			{aside}
		</div>
	)
}

/** Текст, файл, кнопки и настройки рассылки одной карточкой. */
export function MessageSection({
	botId,
	content,
	onContent,
	variables,
	customFlows,
}: {
	botId: string
	content: BroadcastDraftContent
	onContent: (content: BroadcastDraftContent) => void
	variables: { id: string; name: string }[]
	customFlows: { id: string; name: string }[]
}) {
	const length = visibleTextLength(content.text)
	const over = length > MAX_TEXT_LENGTH
	const gallery = content.gallery
	const captioned =
		!!gallery || (!!content.media && !CAPTIONLESS_MEDIA_KINDS.has(content.media.kind))
	// Текст уходит отдельно, если не влезает в подпись или к альбому нужны кнопки.
	const split = captioned && (!fitsCaption(content.text) || (!!gallery && content.buttons.length > 0))
	const source = broadcastMediaSource(botId)

	const setButton = (index: number, patch: Partial<BroadcastDraftButton>) =>
		onContent({
			...content,
			buttons: content.buttons.map((button, i) => (i === index ? { ...button, ...patch } : button)),
		})

	return (
		<Section title="Сообщение" hint="HTML Telegram и ${переменные}, у каждого получателя свои">
			<div>
				<TextWithVariables
					rich="telegram-html"
					rows={6}
					value={content.text}
					maxLength={MAX_TEXT_LENGTH}
					placeholder="Привет, ${firstName}! У нас новости…"
					onChange={(text) => onContent({ ...content, text })}
					variables={variables}
				/>
				<p
					className={cn(
						"mt-1.5 text-xs tabular-nums transition-colors",
						over ? "text-red-400" : "text-muted-foreground",
					)}
				>
					{length} из {captioned && !split ? MAX_CAPTION_LENGTH : MAX_TEXT_LENGTH}
					{captioned && !split ? ". С файлом текст становится подписью" : ""}
				</p>
				<Reveal show={split} className="pt-2">
					<p className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
						{gallery && fitsCaption(content.text)
							? "Telegram не крепит кнопки к альбому: фото уйдут альбомом, а текст с кнопками придёт сразу под ним."
							: `Текст длиннее ${MAX_CAPTION_LENGTH} символов и не влезет в подпись. Файл уйдёт отдельным сообщением, а текст с кнопками придёт сразу под ним.`}
					</p>
				</Reveal>
			</div>

			<div>
				<Part title={gallery ? "Альбом" : "Файл"} />
				<SegmentedControl
					aria-label="Сколько файлов"
					className="mb-2"
					options={MEDIA_MODES}
					value={gallery ? "gallery" : "single"}
					onChange={(value) =>
						onContent(
							value === "gallery"
								? { ...content, media: undefined, gallery: content.media ? [content.media] : [] }
								: { ...content, gallery: undefined, media: gallery?.[0] },
						)
					}
				/>
				{gallery ? (
					<>
						<GalleryField
							source={source}
							gallery={gallery}
							onChange={(next) => onContent({ ...content, gallery: next })}
						/>
						{gallery.length < MIN_GALLERY_ITEMS ? (
							<p className="mt-1.5 text-xs text-muted-foreground">
								В альбоме от {MIN_GALLERY_ITEMS} до {MAX_GALLERY_ITEMS} фото и видео.
							</p>
						) : null}
					</>
				) : (
					<MediaField
						source={source}
						media={content.media}
						onChange={(media) => onContent({ ...content, media })}
					/>
				)}
			</div>

			<div>
				<Part
					title="Кнопки"
					aside={
						<span className="text-xs tabular-nums text-muted-foreground">
							{content.buttons.length} из {MAX_BROADCAST_BUTTONS}
						</span>
					}
				/>
				<ul>
					<AnimatePresence initial={false}>
						{content.buttons.map((button, index) => (
							<motion.li key={button.id} {...listItem}>
								<div className="pb-2">
									<div className="space-y-2 rounded-xl border border-border/60 p-3">
										<div className="flex flex-wrap gap-2 sm:flex-nowrap">
											<Input
												aria-label="Подпись кнопки"
												className="basis-full sm:basis-0 sm:flex-1"
												placeholder="Подпись"
												maxLength={MAX_BUTTON_LABEL_LENGTH}
												value={button.label}
												onChange={(event) => setButton(index, { label: event.target.value })}
											/>
											<NativeSelect
												aria-label="Что делает кнопка"
												className="min-w-0 flex-1 sm:w-48 sm:flex-none"
												value={button.kind}
												onChange={(event) =>
													setButton(
														index,
														event.target.value === "url"
															? { kind: "url", flowId: undefined }
															: { kind: "flow", url: undefined },
													)
												}
											>
												<option value="url">Открыть ссылку</option>
												<option value="flow">Запустить воронку</option>
											</NativeSelect>
											<ToolButton
												label="Удалить кнопку"
												className="hover:bg-red-500/15 hover:text-red-300"
												onClick={() =>
													onContent({
														...content,
														buttons: content.buttons.filter((_, i) => i !== index),
													})
												}
											>
												<Trash2 />
											</ToolButton>
										</div>
										<LoadSwap
											loading={false}
											id={button.kind === "url" ? "url" : customFlows.length > 0 ? "flow" : "no-flows"}
										>
											{button.kind === "url" ? (
												<Input
													aria-label="Ссылка кнопки"
													inputMode="url"
													placeholder="https://…"
													value={button.url ?? ""}
													onChange={(event) => setButton(index, { url: event.target.value.trim() })}
												/>
											) : customFlows.length > 0 ? (
												<NativeSelect
													aria-label="Воронка кнопки"
													value={button.flowId ?? ""}
													onChange={(event) => setButton(index, { flowId: event.target.value })}
												>
													<option value="" disabled>
														Какую воронку запустить
													</option>
													{customFlows.map((flow) => (
														<option key={flow.id} value={flow.id}>
															{flow.name}
														</option>
													))}
												</NativeSelect>
											) : (
												<p className="text-xs text-amber-300">
													Опубликуйте сценарий бота, и кнопка сможет запускать его воронки.
												</p>
											)}
										</LoadSwap>
									</div>
								</div>
							</motion.li>
						))}
					</AnimatePresence>
				</ul>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="rounded-full"
					disabled={content.buttons.length >= MAX_BROADCAST_BUTTONS}
					onClick={() =>
						onContent({
							...content,
							buttons: [
								...content.buttons,
								{ id: makeButtonId(), kind: "url", label: "", url: "" },
							],
						})
					}
				>
					<Plus />
					Добавить кнопку
				</Button>
			</div>

			<div>
				<Part title="Настройки" />
				<div className="flex flex-wrap gap-2">
					{OPTIONS.map((option) => {
						const on = content[option.key] === true
						return (
							<button
								key={option.key}
								type="button"
								aria-pressed={on}
								onClick={() => onContent({ ...content, [option.key]: on ? undefined : true })}
								className={cn(
									"inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors duration-150",
									on
										? "border-primary/50 bg-primary/10 text-foreground"
										: "border-border text-muted-foreground hover:border-foreground/20 hover:text-foreground",
								)}
							>
								<span
									className={cn(
										"grid size-4 place-items-center rounded-full transition-[background-color,transform] duration-150",
										on ? "scale-100 bg-primary text-primary-foreground" : "scale-90 bg-secondary",
									)}
								>
									<Check
										className={cn("size-3 transition-opacity", on ? "opacity-100" : "opacity-0")}
									/>
								</span>
								{option.label}
							</button>
						)
					})}
				</div>
			</div>
		</Section>
	)
}
